import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as action from '../Action/Tickets/Tickets_Action';
import NumberDisplay from "./NumberDisplay/Index";
import { usePermission } from "../hooks/usePermission";
import * as Authservices from '../Services/services';

const Tickets = () => {
    const { can } = usePermission();
    const dispatch = useDispatch();
    const tableRef = useRef(null);
    const dataTableInstanceRef = useRef(null);
    const navigate = useNavigate();
    const [count, setCount] = useState(0);

    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assigningTicketId, setAssigningTicketId] = useState(null);
    const [adminUsers, setAdminUsers] = useState([]);
    const [selectedAdminId, setSelectedAdminId] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);

    const openAssignModal = (ticketId) => {
        setAssigningTicketId(ticketId);
        setSelectedAdminId('');
        setShowAssignModal(true);
        if (adminUsers.length === 0) {
            Authservices.getAdminUsers()
                .then((res) => {
                    const list = res.data?.responseDetails || res.data?.data || [];
                    setAdminUsers(Array.isArray(list) ? list : []);
                })
                .catch(() => toast.error('Failed to load admin users.'));
        }
    };

    const handleAssign = () => {
        if (!selectedAdminId) return;
        setAssignLoading(true);
        Authservices.assignTicket(assigningTicketId, Number(selectedAdminId))
            .then(() => {
                toast.success('Ticket assigned successfully.');
                setShowAssignModal(false);
                if (dataTableInstanceRef.current) {
                    dataTableInstanceRef.current.ajax.reload(null, false);
                }
            })
            .catch((err) => {
                toast.error(err?.response?.data?.responseMessage || 'Failed to assign ticket.');
            })
            .finally(() => setAssignLoading(false));
    };

    useEffect(() => {
        let tableRefCurrent = tableRef.current;
        const $ = window.$ || window.jQuery;

        let currentSize = window.innerWidth <= 450 ? 'mobile' : 'desktop';

        function setPaginationOptions() {
            if (currentSize === 'mobile') {
                $.fn.dataTable.ext.pager.numbers_length = 0;
                return 'simple';
            } else {
                $.fn.dataTable.ext.pager.numbers_length = 5;
                return 'simple_numbers';
            }
        }

        const initDataTable = () => {
            const pagingType = setPaginationOptions();

            if ($ && tableRefCurrent) {
                dataTableInstanceRef.current = $(tableRefCurrent).DataTable({
                    processing: true,
                    serverSide: true,
                    scrollX: true,
                    pagingType: pagingType,
                    ajax: (dataTablesParams, callback) => {
                        const params = {
                            search: dataTablesParams.search.value,
                            page: Math.floor(dataTablesParams.start / dataTablesParams.length) + 1,
                            limit: dataTablesParams.length,
                        };
                        dispatch(action.getTicketList(params)).then((action) => {
                            const rd = action.responseDetails || {};
                            const rows = rd.rows || rd.data || [];
                            const total = rd.total || rd.totalRecords || 0;
                            setCount(total);
                            callback({
                                draw: dataTablesParams.draw,
                                data: rows,
                                recordsTotal: total,
                                recordsFiltered: rd.recordsFiltered || total,
                            });
                        }).catch(() => {
                            callback({ draw: dataTablesParams.draw, data: [], recordsTotal: 0, recordsFiltered: 0 });
                        });
                    },
                    destroy: true,
                    columns: [
                        {
                            data: null,
                            title: 'No',
                            render: function (data, type, row, meta) {
                                return meta.row + meta.settings._iDisplayStart + 1;
                            }
                        },
                        { data: 'ticket_id', title: 'Ticket No.' },
                        { data: 'name', title: 'Name' },
                        { data: 'email', title: 'Email' },
                        { data: 'reason', title: 'Reason' },
                        { data: 'description', title: 'Description' },
                        {
                            data: 'status',
                            title: 'Status',
                            render: function (data, type, row) {
                                return `
                                    <select class="form-select status-dropdown">
                                        <option value="open" ${data === 'open' ? 'selected' : ''}>Open</option>
                                        <option value="in_progress" ${data === 'in_progress' ? 'selected' : ''}>In Progress</option>
                                        <option value="resolved" ${data === 'resolved' ? 'selected' : ''}>Resolved</option>
                                        <option value="closed" ${data === 'closed' ? 'selected' : ''}>Closed</option>
                                    </select>
                                `;
                            }
                        },
                        {
                            data: 'assigned_to',
                            title: 'Assigned To',
                            render: function (data, type, row) {
                                const name = data?.name || '—';
                                const canAssign = window.__canAssignTicket;
                                if (canAssign) {
                                    return `<span class="assign-name">${name}</span> <button class="btn btn-sm assign-ticket-btn" style="font-size:11px;padding:2px 8px;margin-left:4px;" data-id="${row.id}"><i class="fas fa-user-plus"></i></button>`;
                                }
                                return `<span>${name}</span>`;
                            }
                        },
                        {
                            data: 'created_at',
                            title: 'Date',
                            render: function (data, type, row) {
                                if (!data) return '';
                                const date = new Date(data);
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${day}-${month}-${year}`;
                            }
                        }
                    ]
                });

                $(tableRefCurrent).on('preXhr.dt', function () {
                    const processingEl = $('.dataTables_processing');
                    const visibleCols = $(tableRefCurrent).find('thead th:visible').length;
                    if (processingEl.length) {
                        const loaderRow = `
                            <tr class="dt-temp-loader">
                                <td colspan="${visibleCols}" class="text-center">
                                    <div class="dataTables_processing card" style="display:block;margin:0 auto">${processingEl.html()}</div>
                                </td>
                            </tr>
                        `;
                        $(tableRefCurrent).find('tbody').html(loaderRow);
                    }
                });

                $(tableRefCurrent).on('xhr.dt', function () {
                    $(tableRefCurrent).find('tbody .dt-temp-loader').remove();
                });

                $(tableRefCurrent).on('change', '.status-dropdown', function (e) {
                    if (!can('tickets', 'reply')) {
                        toast.error("You don't have permission to update ticket status.");
                        return;
                    }
                    let rowData = dataTableInstanceRef.current.row($(this).closest('tr')).data();
                    dispatch(action.updateTicketStatus({
                        id: rowData.id,
                        status: this.value
                    })).then(() => {
                        if (dataTableInstanceRef.current) {
                            toast.success("Status changed successfully.");
                            dataTableInstanceRef.current.ajax.reload(null, false);
                        }
                    }).catch(() => {
                        toast.error("Something went wrong!");
                    });
                });

                $(tableRefCurrent).on('click', '.assign-ticket-btn', function () {
                    const id = $(this).data('id');
                    openAssignModal(id);
                });
            }
        };

        window.__canAssignTicket = can('tickets', 'assign');
        initDataTable();

        const handleResize = () => {
            const newSize = window.innerWidth <= 450 ? 'mobile' : 'desktop';
            if (newSize !== currentSize) {
                currentSize = newSize;
                if ($.fn.DataTable.isDataTable(tableRefCurrent)) {
                    $(tableRefCurrent).DataTable().destroy();
                }
                initDataTable();
            }
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if ($.fn.DataTable.isDataTable(tableRefCurrent)) {
                $(tableRefCurrent).DataTable().destroy();
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, navigate]);

    return (
        <>
            <section id="users-section" className="section">
                <div className="card">
                    <div className="card-header">
                        <div className="page-title w-100">
                            <div className="d-flex align-items-center justify-content-between">
                                <h4>Ticket List (<span className="total_user">{<NumberDisplay count={count} />}</span>)</h4>
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        <table ref={tableRef} className="user-table table table-striped table-bordered w-100 dataTable no-footer" id="usersTables" role="grid"></table>
                    </div>
                </div>
            </section>

            {showAssignModal && (
                <div className="common-popup active" onClick={(e) => { if (e.target.classList.contains('common-popup')) setShowAssignModal(false); }}>
                    <div className="popup-content" style={{ maxWidth: '420px' }}>
                        <button className="close-btn" onClick={() => setShowAssignModal(false)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                        <h5 style={{ marginBottom: '16px' }}><i className="fas fa-user-plus"></i> Assign Ticket</h5>
                        <div className="form-group" style={{ marginBottom: '24px' }}>
                            <label className="form-label">Select Admin</label>
                            <select
                                className="form-select"
                                value={selectedAdminId}
                                onChange={(e) => setSelectedAdminId(e.target.value)}
                            >
                                <option value="">— Select admin —</option>
                                {adminUsers.map(u => (
                                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                onClick={handleAssign}
                                disabled={!selectedAdminId || assignLoading}
                            >
                                {assignLoading ? <><i className="fas fa-spinner fa-spin"></i> Assigning...</> : 'Assign'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Tickets;
